import { useState, useEffect } from "react";
import { getProjects } from "api/api";
import MainLayout from "layouts/MainLayout";
import ProjectTitle from "components/projects/ProjectTitle";
import ProjectTitleDetailed from "components/projects/ProjectTitleDetailed";
import useQueryParam from "hooks/useQueryParam";

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("type"); // "type" or "tag"
    const [filter, setFilter] = useState("all");
    // const [viewType] = useState("list"); // "list" or "grid" toggle
    const [viewType, setViewType] = useState("list"); // uncomment this if you want to properly enable toggle
    const [selectedParam, setSelectedParam] = useQueryParam("selected");

    // Fetch projects
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const data = await getProjects();
                setProjects(data.projects || data);
            } catch (error) {
                setErrors("Failed to fetch projects");
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, []);

    // Get project tags
    const projectTags = Array.from(
        new Set(projects.flatMap(project => project.tags?.map(t => t.tag || t)))
    ).filter(Boolean);

    // Set filter from URL query param
    useEffect(() => {
        if (!projects.length) return;

        const selected = selectedParam ? decodeURIComponent(selectedParam.replace(/\+/g, ' ')).toLowerCase() : "";

        if (filterType === "tag") {
            const matchedTag = projectTags.find(
                (tag) => (tag || "").toLowerCase() === selected
            );
            setFilter(matchedTag || "all");
        } else {
            const projectTypes = Array.from(new Set(projects.map(p => p.type)));
            const matchedType = projectTypes.find(
                (type) => (type || "").toLowerCase() === selected
            );
            setFilter(matchedType || "all");
        }
    }, [selectedParam, projectTags, projects, filterType]);

    // Filter projects
    const filteredProjects =
        filter === "all"
            ? projects
            : projects.filter((project) => {
                if (filterType === "tag") {
                    return project.tags?.some(t =>
                        (t.tag || t || "").toLowerCase() === (filter || "").toLowerCase()
                    );
                } else {
                    return (project.type || "").toLowerCase() === (filter || "").toLowerCase();
                }
            });

    const handleFilterChange = (tag) => {
        setFilter(tag || "all");
        setSelectedParam(tag === "all" ? null : encodeURIComponent(tag).replace(/%20/g, '+'));
    };

    return (
        <MainLayout>
            {loading && <p>Thinking...</p>}
            {errors && <p style={{ color: "red" }}>{errors}</p>}

            {!loading && !errors && (
                <div className="grid grid-cols-12">
                    {/* Title */}
                    <div className="col-span-12 mt-10 md:mt-5 md:px-5 md:pr-8 flex flex-col md:flex-row items-center justify-center md:justify-between space-y-4 md:space-y-0 md:space-x-8">
                        <h1 className="text-[3rem] md:text-[5rem] text-center md:text-left text-pink-400">
                            All Projects
                        </h1>
                        {/* Toggle View Button */}
                        <div className="col-span-12 flex justify-center mb-10">
                            <button
                                onClick={() => setViewType(viewType === "list" ? "grid" : "list")}
                                className="px-6 py-2 bg-pink-400 text-white rounded-full hover:bg-pink-500 transition"
                            >
                                {viewType === "list" ? "Click for Detailed View" : "Compact View"}
                            </button>
                        </div>
                    </div>

                    {/* Filter type tabs */}
                    <div className="mt-6 mb-8 ml-4 col-span-12 flex gap-4 px-4 md:px-0 justify-center md:justify-center">
                        <button
                            onClick={() => {
                                setFilterType("type");
                                setFilter("all");
                                setSelectedParam(null);
                            }}
                            className={`cursor-pointer px-6 py-2 font-medium transition ${filterType === "type"
                                ? "text-pink-400 border-b-2 border-pink-400"
                                : "text-gray-500 hover:text-pink-300"
                                }`}
                        >
                            Filter by Type
                        </button>
                        <button
                            onClick={() => {
                                setFilterType("tag");
                                setFilter("all");
                                setSelectedParam(null);
                            }}
                            className={`cursor-pointer px-6 py-2 font-medium transition ${filterType === "tag"
                                ? "text-pink-400 border-b-2 border-pink-400"
                                : "text-gray-500 hover:text-pink-300"
                                }`}
                        >
                            Filter by Tags
                        </button>
                    </div>

                    {/* Filter badges */}
                    <div className="mb-8 ml-4 md:mb-10 col-span-12 flex flex-wrap gap-3 gap-y-6 px-4 md:px-80 justify-center">
                        <button
                            onClick={() => handleFilterChange("all")}
                            className={`cursor-pointer px-4 py-1 rounded-full border transition ${filter === "all"
                                ? "bg-pink-400 text-white border-pink-400"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-pink-100"
                                }`}
                        >
                            All
                        </button>

                        {(filterType === "tag" ? projectTags : Array.from(new Set(projects.map(p => p.type))))
                            .filter(Boolean)
                            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) // <-- sort alphabetically
                            .map((item) => {
                                const isActive = (filter || "").toLowerCase() === (item || "").toLowerCase();

                                return (
                                    <button
                                        key={item}
                                        onClick={() => handleFilterChange(item)}
                                        className={`cursor-pointer px-4 py-1 rounded-full border transition ${isActive
                                            ? "bg-pink-400 text-white border-pink-400"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-pink-100"
                                            }`}
                                    >
                                        {item}
                                    </button>
                                );
                            })}
                    </div>


                    {/* Conditionally Render Project Lists */}
                    {viewType === "list" ? (
                        <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-5 m-auto px-6 md:px-4 ">
                            {filteredProjects.map((project, index) => {
                                const delay = (index + 1) * 50;
                                return <ProjectTitle key={project.id || index} project={project} delay={delay} />;
                            })}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 min-w-[100vw] gap-5 gap-y-10 md:gap-y-20 px-5">
                            {filteredProjects.map((project, index) => {
                                const delay = (index + 1) * 50;
                                return <ProjectTitleDetailed key={project.id || index} project={project} delay={delay} />;
                            })}
                        </div>
                    )}
                </div>
            )}
        </MainLayout>
    );
};

export default Projects;
